/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, take, takeEvery, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { notificationInit } from '../../modules/notifications/actions';
import { REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import {
	GET_GROUPS_LIST,
	GET_TYPES,
	setGroupsListAction,
	setIsGroupsListLoadingAction,
	setIsTypesLoadingAction,
	setTypesAction,
	setIsModesLoadingAction,
	setModesAction,
	GET_MODES,
	GET_WAYS_LIST,
	setIsWaysListLoadingAction,
	setWaysListAction,
	GET_COLLECTION_GROUP_LIST,
	GET_COLLECTION_WAY_LIST,
	getGroupsListAction,
	setIsLoadingAction,
	setWaysGroupListAction,
	setIsWaysGroupListLoadingAction,
	resetIsWaysGroupListLoadingAction,
	GET_WAYS_GROUP_LIST,
	GROUP_REGROUP,
	ORDERS_REGROUP,
	setIsCollectionGroupListLoadingAction,
	setCollectionGroupListAction,
	resetIsCollectionGroupListLoadingAction,
	setIsCollectionWayListLoadingAction,
	setCollectionWayListAction,
	resetIsCollectionWayListLoadingAction,
	getCollectionGroupListAction,
	GET_ORDERS_COLLECTION_GROUP_LIST,
	GET_ORDERS_COLLECTION_WAY_LIST,
	resetIsOrdersCollectionWayListLoadingAction,
	setOrdersCollectionWayListAction,
	setIsOrdersCollectionWayListLoadingAction,
	resetIsOrdersCollectionGroupListLoadingAction,
	setOrdersCollectionGroupListAction,
	setIsOrdersCollectionGroupListLoadingAction,
	setIsGroupsWithWaysListLoadingAction,
	setGroupsWithWaysListAction,
	GET_GROUPS_WITH_WAYS_LIST,
	getGroupsWithWaysListAction,
	getCollectionWayListAction,
	getWaysGroupListAction,
	GROUP_RESET,
	getWaysListAction,
	getOrdersCollectionGroupListAction,
	getOrdersCollectionWayListAction,
} from './actions';
import {
	getGroupWaysListRequest,
	getModesDictionaryRequest,
	getOrderGroupsTypesDictionaryRequest,
	getOrdersGroupsListRequest,
	getWaysListRequest,
	ordersRegroupRequest,
	orderGroupRegroupRequest,
	getGroupCollectionListRequest,
	getWayCollectionListRequest,
	getGroupCollectionOrdersListRequest,
	getWayCollectionOrdersListRequest,
	getOrdersGroupsWithWaysListRequest,
	orderGroupResetRequest,
} from '../../api/requests';
import { removeModalQuery } from '../../utils/removeModalQuery';
import {
	getCollectionGroupOpenSelector,
	getCollectionWayOpenSelector,
	getGroupOpenSelector,
	getGroupWayOpenSelector,
	getWayOpenSelector,
} from './selectors';
import { getCompanyIdSelector } from '../profile/selectors';
import { createOrdersGroupsUpdateChannels } from '../../api/sockets';
import { getOrdersPlanningsListAction } from '../orders-plannings/actions';
import { getProcessInfoAction, getPlanningsProcessListAction } from '../orders-plannings-process/actions';
import { getAddressLink } from '../../utils/getAddressLink';

export function* getGroupsListSaga({ payload: { searchQuery, noLoading } }) {
	try {
		if (!noLoading) {
			yield put(setIsGroupsListLoadingAction(true));
		}

		const { data, message, toast } = yield call(getOrdersGroupsListRequest, searchQuery ? `?${searchQuery}` : '');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setGroupsListAction(
					data?.map(group => ({
						...group,
						link_process: group.process
							? `?${searchQuery ? `${searchQuery}&` : ''}modal=plannings-process&id=${group.process.id}`
							: null,
						link:
							group.id !== 1 && !group.process
								? `?${searchQuery ? `${searchQuery}&` : ''}modal=order-group&id=${group.id}`
								: null,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsGroupsListLoadingAction(false));
	}
}

export function* getGroupsWithWaysListSaga({ payload: { searchQuery, noLoading } }) {
	try {
		if (!noLoading) {
			yield put(setIsGroupsWithWaysListLoadingAction(true));
		}

		const { data, message, toast } = yield call(
			getOrdersGroupsWithWaysListRequest,
			searchQuery ? `?${searchQuery}` : '',
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setGroupsWithWaysListAction(
					data?.map(group => ({
						...group,
						link:
							group.id !== 1 && !group.process
								? `?${searchQuery ? `${searchQuery}&` : ''}modal=order-group&id=${group.id}`
								: null,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsGroupsWithWaysListLoadingAction(false));
	}
}

export function* getWaysListSaga() {
	try {
		yield put(setIsWaysListLoadingAction(true));

		const { data, message, toast } = yield call(getWaysListRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setWaysListAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsWaysListLoadingAction(false));
	}
}

export function* getTypesSaga() {
	try {
		yield put(setIsTypesLoadingAction(true));

		const { data, message, toast } = yield call(getOrderGroupsTypesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTypesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTypesLoadingAction(false));
	}
}

export function* getCollectionGroupListSaga({ payload: { searchQuery, noLoading, row } }) {
	try {
		if (!noLoading) {
			yield put(setIsCollectionGroupListLoadingAction(row.id));
		}

		const query = new URLSearchParams(searchQuery);
		query.delete('filters');
		query.delete('sorts');

		const str = removeModalQuery(
			query.toString().replace('childrenSorts', 'sorts').replace('childrenFilters', 'filters'),
		);

		const { data, message, toast } = yield call(
			getGroupCollectionListRequest,
			row.id,
			row.process?.id,
			`?${str ? `${str}&receive=all` : 'receive=all'}`,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setCollectionGroupListAction(
					row.id,
					data?.map(collection => ({
						...collection,
						points: collection.points.map(point => ({
							...point,
							address: getAddressLink(searchQuery, point.address),
						})),
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(resetIsCollectionGroupListLoadingAction(row.id));
	}
}

export function* getOrdersCollectionGroupListSaga({ payload: { searchQuery, noLoading, row, collection } }) {
	try {
		if (!noLoading) {
			yield put(setIsOrdersCollectionGroupListLoadingAction(collection.id));
		}

		const query = new URLSearchParams(searchQuery);
		query.delete('filters');
		query.delete('sorts');

		const str = removeModalQuery(
			query.toString().replace('childrenSorts', 'sorts').replace('childrenFilters', 'filters'),
		);

		const { data, message, toast } = yield call(
			getGroupCollectionOrdersListRequest,
			row.id,
			row.process?.id,
			collection.id,
			`?${str ? `${str}&receive=all` : 'receive=all'}`,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const search = removeModalQuery(searchQuery);

			yield put(
				setOrdersCollectionGroupListAction(
					collection.id,
					data?.map(order => ({
						...order,
						points: order.points.map(point => ({
							...point,
							address: getAddressLink(searchQuery, point.address),
						})),
						link: `?${search ? `${search}&` : ''}modal=order&id=${order.id}`,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(resetIsOrdersCollectionGroupListLoadingAction(collection.id));
	}
}

export function* getWaysGroupListSaga({ payload: { searchQuery, noLoading, row } }) {
	try {
		if (!noLoading) {
			yield put(setIsWaysGroupListLoadingAction(row.id));
		}

		const query = new URLSearchParams(searchQuery);
		query.delete('filters');
		query.delete('sorts');

		const str = removeModalQuery(
			query.toString().replace('childrenSorts', 'sorts').replace('childrenFilters', 'filters'),
		);

		const { data, message, toast } = yield call(
			getGroupWaysListRequest,
			row.id,
			row.planning?.id,
			`?${str ? `${str}&receive=all` : 'receive=all'}`,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setWaysGroupListAction(
					row.id,
					data?.map(way => ({
						...way,
						link: `?${searchQuery ? `${searchQuery}&` : ''}modal=order-way&id=${way.id}`,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(resetIsWaysGroupListLoadingAction(row.id));
	}
}

export function* getCollectionWayListSaga({ payload: { searchQuery, noLoading, row } }) {
	try {
		if (!noLoading) {
			yield put(setIsCollectionWayListLoadingAction(row.id));
		}

		const query = new URLSearchParams(searchQuery);
		query.delete('filters');
		query.delete('sorts');

		const str = removeModalQuery(
			query.toString().replace('childrenSorts', 'sorts').replace('childrenFilters', 'filters'),
		);

		const { data, message, toast } = yield call(
			getWayCollectionListRequest,
			row.id,
			`?${str ? `${str}&receive=all` : 'receive=all'}`,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setCollectionWayListAction(
					row.id,
					data?.map(collection => ({
						...collection,
						points: collection.points.map(point => ({
							...point,
							address: getAddressLink(searchQuery, point.address),
						})),
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(resetIsCollectionWayListLoadingAction(row.id));
	}
}

export function* getOrdersCollectionWayListSaga({ payload: { searchQuery, noLoading, row, collection } }) {
	try {
		if (!noLoading) {
			yield put(setIsOrdersCollectionWayListLoadingAction(collection.id));
		}

		const query = new URLSearchParams(searchQuery);
		query.delete('filters');
		query.delete('sorts');

		const str = removeModalQuery(
			query.toString().replace('childrenSorts', 'sorts').replace('childrenFilters', 'filters'),
		);

		const { data, message, toast } = yield call(
			getWayCollectionOrdersListRequest,
			row.id,
			collection.id,
			`?${str ? `${str}&receive=all` : 'receive=all'}`,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setOrdersCollectionWayListAction(
					collection.id,
					data?.map(order => ({
						...order,
						points: order.points.map(point => ({
							...point,
							address: getAddressLink(searchQuery, point.address),
						})),
						link: `?${searchQuery ? `${searchQuery}&` : ''}modal=order&id=${order.id}`,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(resetIsOrdersCollectionWayListLoadingAction(collection.id));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setModesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* groupRegroupSaga({ payload: { searchQuery, row } }) {
	try {
		yield put(setIsGroupsListLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const { data, message, toast } = yield call(orderGroupRegroupRequest, row.id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));

			if (groupOpen) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsGroupsListLoadingAction(false));
	}
}

export function* groupResetSaga({ payload: { searchQuery, row } }) {
	try {
		yield put(setIsGroupsWithWaysListLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const { data, message, toast } = yield call(orderGroupResetRequest, row.id, row.process?.id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (groupOpen) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsGroupsWithWaysListLoadingAction(false));
	}
}

export function* ordersRegroupSaga({ payload: { searchQuery, collection, orders } }) {
	try {
		yield put(setIsLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const { data, message, toast } = yield call(ordersRegroupRequest, { collection, orders });

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));

			if (groupOpen) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLoadingAction(false));
	}
}

export function* socketOrdersGroupsTableUpdateSaga() {
	const companyId = yield select(getCompanyIdSelector());

	const channel = yield call(() => createOrdersGroupsUpdateChannels(companyId));

	if (!channel) return;

	while (true) {
		const { companyId, groupIds, wayIds, collectionIds } = yield take(channel);

		const query = new URLSearchParams(window.location.search);

		const searchQuery = removeModalQuery(query.toString());

		if (query.has('modal', 'plannings-process')) {
			yield put(
				getPlanningsProcessListAction({
					process_id: query.get('id'),
					searchQuery: removeModalQuery(query.toString()),
					noLoading: true,
				}),
			);
			yield put(getProcessInfoAction({ process_id: query.get('id'), noLoading: true }));
		}

		if (companyId && window.location.pathname.includes('orders_groups')) {
			const groupOpen = yield select(getGroupOpenSelector());

			const groupWayOpen = yield select(getGroupWayOpenSelector());

			const wayOpen = yield select(getWayOpenSelector());

			const collectionGroupOpen = yield select(getCollectionGroupOpenSelector());

			const collectionWayOpen = yield select(getCollectionWayOpenSelector());

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));

			yield put(getWaysListAction());

			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (groupOpen && groupIds && groupIds.includes(groupOpen.id)) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery, noLoading: true }));

				if (collectionGroupOpen && collectionIds.includes(collectionGroupOpen.id)) {
					yield put(
						getOrdersCollectionGroupListAction({
							row: groupOpen,
							collection: collectionGroupOpen,
							searchQuery,
							noLoading: true,
						}),
					);
				}
			}

			if (wayOpen && wayIds && wayIds.includes(wayOpen.id)) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery, noLoading: true }));

				if (collectionWayOpen && collectionIds.includes(collectionWayOpen.id)) {
					yield put(
						getOrdersCollectionWayListAction({
							row: wayOpen,
							collection: collectionWayOpen,
							searchQuery,
							noLoading: true,
						}),
					);
				}
			}

			if (groupWayOpen && groupIds && groupIds.includes(groupWayOpen.id)) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}
		} else if (companyId && window.location.pathname.includes('orders_plannings')) {
			yield put(getOrdersPlanningsListAction({ searchQuery, noLoading: true }));
		}
	}
}

export default function* orderGroupsContainerSaga() {
	yield takeEvery(GET_GROUPS_LIST, getGroupsListSaga);
	yield takeEvery(GET_GROUPS_WITH_WAYS_LIST, getGroupsWithWaysListSaga);
	yield takeEvery(GET_TYPES, getTypesSaga);
	yield takeEvery(GET_COLLECTION_GROUP_LIST, getCollectionGroupListSaga);
	yield takeEvery(GET_ORDERS_COLLECTION_GROUP_LIST, getOrdersCollectionGroupListSaga);
	yield takeEvery(GET_WAYS_GROUP_LIST, getWaysGroupListSaga);
	yield takeEvery(GET_COLLECTION_WAY_LIST, getCollectionWayListSaga);
	yield takeEvery(GET_ORDERS_COLLECTION_WAY_LIST, getOrdersCollectionWayListSaga);
	yield takeEvery(GET_WAYS_LIST, getWaysListSaga);
	yield takeEvery(GET_MODES, getModesSaga);
	yield takeEvery(GROUP_REGROUP, groupRegroupSaga);
	yield takeEvery(ORDERS_REGROUP, ordersRegroupSaga);
	yield takeEvery(GROUP_RESET, groupResetSaga);
}
